import React, { useState } from "react";
import { DateTime } from "luxon";
import { TCaseTimelineForm, TCaseTimelineRoadblockContext } from "../../../../domain/codecs/form/CaseTimelineForm";
import { TChatsMessageForm } from "../../../../domain/codecs/form/ChatsMessageForm";
import { buildTimelineRows, TTimelineFilter, TTimelineNoteCluster } from "./CRMCaseTimelineGroupingLogic";
import { CRMCaseTimelineNote, TTimelineNoteEdit, TTimelineNotePin } from "./CRMCaseTimelineNote";
import { CRMCaseTimelineEventLine } from "./CRMCaseTimelineEventLine";
import { CRMCaseTimelineMilestone } from "./CRMCaseTimelineMilestone";
import { CRMCaseTimelineComposer } from "./CRMCaseTimelineComposer";
import { CRMCardOutsideLabelled } from "../CRMCardOutsideLabelled/CRMCardOutsideLabelled";
import { CRMRoadblockMessagePlaceholder } from "../CRMRoadblock/CRMRoadblockMessagePlaceholder/CRMRoadblockMessagePlaceholder";
import { SpacingColumn } from "../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../BuildingBlocks/SpacingRow";
import { Background } from "../BuildingBlocks/Background";
import { Padding } from "../BuildingBlocks/Padding";
import { HorizontalLine } from "../BuildingBlocks/HorizontalLine";
import { FontQuicksand } from "../BuildingBlocks/FontQuicksand";
import { FontSize } from "../BuildingBlocks/FontSize";
import { Text } from "../BuildingBlocks/Text";
import { Uppercase } from "../Uppercase/Uppercase";
import { WeightBold } from "../WeightBold/WeightBold";
import { CRMIcon } from "../CRMIcon/CRMIcon";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMColors } from "../../models/CRMColors";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { CRMParagraph } from "../Simple/CRMParagraph/CRMParagraph";

type TCaseTimelineCardProps = {
    form: TCaseTimelineForm;
    onTypeMessage: (form: TChatsMessageForm) => void;
    onSubmitMessage: (form: TChatsMessageForm) => void;
    onSelectRoadblock: (target: { roadblock_id: string, chats_id: string }) => void;
    onOpenRoadblock: (roadblock: TCaseTimelineRoadblockContext) => void;
    onEditNote: (payload: TTimelineNoteEdit) => void;
    onPinNote: (payload: TTimelineNotePin) => void;
};

const dayLabel = (date: string): string =>
    DateTime.fromISO(date).setZone("Europe/London").toISODate() === DateTime.utc().setZone("Europe/London").toISODate()
        ? "Today"
        : DateTime.fromISO(date).setZone("Europe/London").toFormat("d MMM");

export const CRMCaseTimelineCard = (props: React.PropsWithChildren<TCaseTimelineCardProps>): JSX.Element => {

    const [filter, setFilter] = useState<TTimelineFilter>("notes");

    const IsLoading = props.form.status === "loading";
    const HasErrored = props.form.status === "failure" || props.form.status === "validationError";
    const { pinned, rows: Rows } = buildTimelineRows(props.form.children.entries, filter);
    const HasEntries = props.form.children.entries.length > 0;

    const renderNote = (cluster: TTimelineNoteCluster, key: React.Key): JSX.Element => (
        <CRMCaseTimelineNote
            key={key}
            participants={props.form.children.participants}
            cluster={cluster}
            currentUserId={props.form.children.current_user_id}
            onOpenRoadblock={props.onOpenRoadblock}
            onEditNote={props.onEditNote}
            onPinNote={props.onPinNote}
        />
    );

    return (
        <CRMCardOutsideLabelled labelColor={"highlights-calming-pink-4"} shadow={true}>
            <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                {/* HEADER: title + filter toggle */}
                <SpacingRow spacing={CRMSpacing.MEDIUM} alignItems="center" childSize="1fr auto">
                    <SpacingRow spacing={CRMSpacing.TINY} alignItems="center">
                        <CRMIcon iconName="chat-bubble" size="small" colour="neutral-ink" />
                        <FontQuicksand>
                            <FontSize size={CRMFontSizes.SMALL} lineHeight="1.2">
                                <Uppercase><WeightBold>Timeline</WeightBold></Uppercase>
                            </FontSize>
                        </FontQuicksand>
                    </SpacingRow>

                    {HasEntries && !IsLoading && !HasErrored &&
                        <Background color={CRMColors.NEUTRAL_12} borderRadius="9px">
                            <Padding type="custom" spacing="3px">
                                <SpacingRow spacing="2px">
                                    <FilterTab label="Notes" isOn={filter === "notes"} onClick={() => setFilter("notes")} />
                                    <FilterTab label="Everything" isOn={filter === "all"} onClick={() => setFilter("all")} />
                                </SpacingRow>
                            </Padding>
                        </Background>
                    }
                </SpacingRow>

                {/* COMPOSER (top) */}
                {!IsLoading && !HasErrored &&
                    <CRMCaseTimelineComposer
                        form={props.form.children.chat_message_form}
                        roadblockOptions={props.form.children.roadblock_options}
                        participants={props.form.children.participants}
                        awaitableEntities={props.form.children.awaitable_entities}
                        onTypeMessage={props.onTypeMessage}
                        onSubmitMessage={props.onSubmitMessage}
                        onSelectRoadblock={props.onSelectRoadblock}
                    />
                }

                {/* LOADING / ERROR */}
                {IsLoading &&
                    <SpacingColumn spacing={CRMSpacing.LARGE}>
                        <CRMRoadblockMessagePlaceholder />
                        <CRMRoadblockMessagePlaceholder />
                    </SpacingColumn>
                }
                {HasErrored &&
                    <CRMParagraph>
                        The timeline didn't load. Reopen the case to try again. If it keeps happening, let the development team know.
                    </CRMParagraph>
                }

                {/* FEED (newest-first) */}
                {!IsLoading && !HasErrored &&
                    <div className="crm-case-timeline-card__body">
                        {!HasEntries &&
                            <CRMParagraph>
                                Nothing on the timeline yet. Notes and roadblock updates will show here as the case moves along.
                            </CRMParagraph>
                        }

                        {/* PINNED BAND — communal pins lifted to the top */}
                        {pinned.length > 0 &&
                            <SpacingColumn spacing="2px">
                                <Padding type="custom" spacing={`${CRMSpacing.MEDIUM} ${CRMSpacing.MEDIUM} ${CRMSpacing.TINY}`}>
                                    <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center" childSize="auto 1fr">
                                        <SpacingRow spacing={CRMSpacing.TINY} alignItems="center">
                                            <CRMIcon iconName="pinned" size="14px" colour="neutral-4" />
                                            <FontQuicksand>
                                                <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_4} weight={700}>
                                                    Pinned
                                                </Text>
                                            </FontQuicksand>
                                        </SpacingRow>
                                        <HorizontalLine color={CRMColors.NEUTRAL_10} />
                                    </SpacingRow>
                                </Padding>
                                {pinned.map((cluster, index) => renderNote(cluster, `pinned-${index}`))}
                            </SpacingColumn>
                        }

                        {HasEntries &&
                            <SpacingColumn spacing="2px">
                                {Rows.map((row, index) => {
                                    switch (row.kind) {
                                        case "day":
                                            return (
                                                <Padding key={index} type="custom" spacing={`${CRMSpacing.MEDIUM} ${CRMSpacing.MEDIUM} ${CRMSpacing.TINY}`}>
                                                    <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center" childSize="auto 1fr">
                                                        <FontQuicksand>
                                                            <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_4} weight={700}>
                                                                {dayLabel(row.date)}
                                                            </Text>
                                                        </FontQuicksand>
                                                        <HorizontalLine color={CRMColors.NEUTRAL_10} />
                                                    </SpacingRow>
                                                </Padding>
                                            );
                                        case "note":
                                            return renderNote(row, index);
                                        case "event":
                                            return (
                                                <CRMCaseTimelineEventLine
                                                    key={index}
                                                    participants={props.form.children.participants}
                                                    event={row}
                                                    onOpenRoadblock={props.onOpenRoadblock}
                                                />
                                            );
                                        case "milestone":
                                            return <CRMCaseTimelineMilestone key={index} entry={row.entry} />;
                                    }
                                })}
                            </SpacingColumn>
                        }
                    </div>
                }
            </SpacingColumn>
        </CRMCardOutsideLabelled>
    );
};

const FilterTab = (props: { label: string; isOn: boolean; onClick: () => void }): JSX.Element => (
    <Background
        color={props.isOn ? CRMColors.NEUTRAL_PAPER : undefined}
        borderRadius="7px"
        cursor="pointer"
        onClick={props.onClick}
    >
        <Padding type="custom" spacing={`5px ${CRMSpacing.SMALL}`}>
            <FontQuicksand>
                <Text size={CRMFontSizes.SMALL} color={props.isOn ? CRMColors.NEUTRAL_INK : CRMColors.NEUTRAL_2} weight={600}>
                    {props.label}
                </Text>
            </FontQuicksand>
        </Padding>
    </Background>
);
